import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Drawer,
  Box,
  Typography,
  CircularProgress,
  Grid,
  FormControl,
  Autocomplete,
  TypographyProps,
  TextField,
  Button,
  AutocompleteRenderInputParams,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import _ from 'lodash';

import {
  defaultEmptySelectedOption,
  defaultSelectedDataOption,
  ISelectedDataOption,
} from '../../../../../constants';
import { useAppSelector } from '../../../../../hooks';
import { IQuestionSubTopic } from '../../../../../store/QuestionSlice';
import { getTopics, getTopicState } from '../../../../../store/TopicSlice';
import {
  getCurriculumState,
  getCurriculums,
  getSubjectMapDataBySubjectCode,
} from '../../../../../store/CurriculumSlice';
import {
  adminGetSubTopicList,
  getSubTopicState,
} from '../../../../../store/SubTopicSlice';
import { constructOptionsFromArray } from '../../../../../helper';

interface ISelectedOption {
  label: string;
  value: string;
}

interface IAddNewSubTopicDrawer {
  open: boolean;
  onClose: () => void;
  addSubTopic: (questionSubTopics: IQuestionSubTopic[]) => void;
}

const label: TypographyProps = {
  variant: 'subtitle2',
  fontWeight: 600,
  sx: { mb: '5px' },
};

const defaultSubTopicsFilterData = {
  limit: 1000,
  offset: 0,
};

const AddNewSubTopicDrawer: React.FC<IAddNewSubTopicDrawer> = ({
  open,
  onClose,
  addSubTopic,
}) => {
  const dispatch = useDispatch();

  // Store
  const curriculumState = useAppSelector(getCurriculumState);
  const topicState = useAppSelector(getTopicState);
  const subTopicState = useAppSelector(getSubTopicState);

  // State
  const [papers, setPapers] = useState<string[]>([]);
  const [curriculumData, setCurriculumData] = useState<ISelectedDataOption>({
    ...defaultSelectedDataOption,
  });

  const [gradeData, setGradeData] = useState<ISelectedDataOption>({
    ...defaultSelectedDataOption,
  });

  const [subjectData, setSubjectData] = useState<ISelectedDataOption>({
    ...defaultSelectedDataOption,
  });

  const [subjectLevelData, setSubjectLevelData] = useState<ISelectedDataOption>(
    { ...defaultSelectedDataOption }
  );

  const [topicData, setTopicData] = useState<ISelectedDataOption>({
    ...defaultSelectedDataOption,
  });

  const [subTopicData, setSubTopicData] = useState<ISelectedDataOption>({
    ...defaultSelectedDataOption,
  });

  const subjectMapData = curriculumState.subjectMapDataForSubjectCode;

  useEffect(() => {
    dispatch(getCurriculums());
  }, []);

  useEffect(() => {
    onSubjectMapChange();
  }, [curriculumState.list]);

  useEffect(() => {
    if (
      subjectMapData &&
      subjectMapData.subjectLevels &&
      subjectMapData.subjectLevels.length
    ) {
      const newSubjectLevelData: ISelectedDataOption = {
        ...subjectLevelData,
        selectedItem: defaultEmptySelectedOption,
      };

      const newTopicData: ISelectedDataOption = {
        ...defaultSelectedDataOption,
      };

      newSubjectLevelData.options = constructOptionsFromArray(
        subjectMapData.subjectLevels,
        'name',
        'name',
        ['subjectLevels']
      );

      if (newSubjectLevelData.options.length) {
        // eslint-disable-next-line prefer-destructuring
        newSubjectLevelData.selectedItem = newSubjectLevelData.options[0];
      }

      if (subjectData.selectedItem.value) {
        dispatch(
          getTopics({
            subjectCode: subjectData.selectedItem.value,
            subjectLevel: newSubjectLevelData.selectedItem.label,
          })
        );
      }

      setSubjectLevelData(newSubjectLevelData);
      setTopicData(newTopicData);
    }
  }, [subjectMapData]);

  useEffect(() => {
    if (topicState.list && topicState.list.length) {
      const newTopicData: ISelectedDataOption = {
        ...topicData,
        selectedItem: defaultEmptySelectedOption,
      };

      newTopicData.options = _.sortBy(
        [
          ...constructOptionsFromArray(topicState.list, 'name', '_id', [
            'sortingOrder',
            'papers',
          ]),
        ],
        ['sortingOrder', 'label']
      );

      setTopicData(newTopicData);
    }
  }, [topicState.list]);

  useEffect(() => {
    if (subTopicState.list && subTopicState.list.length) {
      const newSubTopicData: ISelectedDataOption = {
        ...subTopicData,
        selectedItem: defaultEmptySelectedOption,
      };

      newSubTopicData.options = constructOptionsFromArray(
        subTopicState.list,
        'name',
        '_id'
      );

      setSubTopicData(newSubTopicData);
    }
  }, [subTopicState.list]);

  const onSubjectMapChange = (data?: {
    curriculumOption?: ISelectedOption;
    gradeOption?: ISelectedOption;
    subjectOption?: ISelectedOption;
  }) => {
    const newCurriculumData: ISelectedDataOption = { ...curriculumData };
    const newGradeData: ISelectedDataOption = { ...gradeData };
    const newSubjectData: ISelectedDataOption = { ...subjectData };

    if (curriculumState.list && curriculumState.list.length) {
      newCurriculumData.options = constructOptionsFromArray(
        curriculumState.list,
        'name',
        'name'
      );

      if (data?.curriculumOption) {
        newCurriculumData.selectedItem = data.curriculumOption;
      } else {
        // eslint-disable-next-line prefer-destructuring
        newCurriculumData.selectedItem = newCurriculumData.options[0];
      }

      const selectedCurriculum = curriculumState.list.find(
        (curriculumItem) =>
          curriculumItem.name === newCurriculumData.selectedItem.label
      );

      if (
        selectedCurriculum &&
        selectedCurriculum.grades &&
        selectedCurriculum.grades.length
      ) {
        newGradeData.options = constructOptionsFromArray(
          selectedCurriculum.grades,
          'name',
          'name'
        );

        if (data?.gradeOption) {
          newGradeData.selectedItem = data.gradeOption;
        } else {
          // eslint-disable-next-line prefer-destructuring
          newGradeData.selectedItem = newGradeData.options[0];
        }

        const selectedGrade = selectedCurriculum.grades.find(
          (gradeItem) => gradeItem.name === newGradeData.selectedItem.label
        );

        if (
          selectedGrade &&
          selectedGrade.subjects &&
          selectedGrade.subjects.length
        ) {
          newSubjectData.options = _.sortBy(
            constructOptionsFromArray(selectedGrade.subjects, 'name', 'code'),
            'label'
          );

          if (data?.subjectOption) {
            newSubjectData.selectedItem = data.subjectOption;
          } else {
            // eslint-disable-next-line prefer-destructuring
            newSubjectData.selectedItem = newSubjectData.options[0];
          }

          dispatch(
            getSubjectMapDataBySubjectCode({
              subjectCode: newSubjectData.selectedItem.value,
            })
          );
        }
      }
    }

    setCurriculumData(newCurriculumData);
    setGradeData(newGradeData);
    setSubjectData(newSubjectData);
  };

  const handleSubjectChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: ISelectedOption
  ) => {
    const newSubjectData: ISelectedDataOption = {
      ...subjectData,
      selectedItem: value,
    };

    setSubjectData(newSubjectData);

    dispatch(
      getSubjectMapDataBySubjectCode({
        subjectCode: newSubjectData.selectedItem.value,
      })
    );
  };

  const handleSubjectLevelChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: ISelectedOption
  ) => {
    const newSubjectLevelData: ISelectedDataOption = {
      ...subjectLevelData,
      selectedItem: value,
    };

    dispatch(
      getTopics({
        subjectCode: subjectData.selectedItem.value,
        subjectLevel: newSubjectLevelData.selectedItem.label,
      })
    );

    setSubjectLevelData(newSubjectLevelData);
  };

  const handleTopicChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: ISelectedOption
  ) => {
    const newTopicData: ISelectedDataOption = {
      ...topicData,
      selectedItem: value,
    };

    dispatch(
      adminGetSubTopicList({
        ...defaultSubTopicsFilterData,
        topicId: value.value,
      })
    );

    setTopicData(newTopicData);
  };

  const handleSubTopicChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: ISelectedOption
  ) => {
    const newSubTopicData: ISelectedDataOption = {
      ...subTopicData,
      selectedItem: value,
    };

    setSubTopicData(newSubTopicData);
  };

  const handlePaperChange = (paperItem: { name: string }, isChecked) => {
    let newPapers = [...papers];

    if (isChecked) {
      newPapers = newPapers.filter((paper) => paper !== paperItem.name);
    } else {
      newPapers.push(paperItem.name);
    }

    setPapers(newPapers);
  };

  const onSubmit = () => {
    const newSubTopics: IQuestionSubTopic[] = [];

    papers.forEach((paper) => {
      if (subTopicData.selectedItem && subTopicData.selectedItem.value) {
        const subTopicItem = subTopicState.list.find(
          (subTopicListItem) =>
            subTopicListItem._id === subTopicData.selectedItem.value
        );

        if (subTopicItem) {
          newSubTopics.push({
            paper,
            subTopic: subTopicItem,
          });
        }
      }
    });

    addSubTopic(newSubTopics);

    onClose();
  };

  const renderInput = (data: {
    params: AutocompleteRenderInputParams;
    isLoading: boolean;
    placeholder?: string;
  }) => (
    <TextField
      {...data.params}
      placeholder={data.placeholder}
      InputProps={{
        ...data.params.InputProps,
        endAdornment: (
          <>
            {data.isLoading ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              data.params.InputProps.endAdornment
            )}
          </>
        ),
      }}
    />
  );

  return (
    <Drawer
      open={open}
      anchor="right"
      onClose={onClose}
      PaperProps={{ style: { minWidth: '400px', maxWidth: '400px' } }}
    >
      <Box role="presentation" style={{ padding: '30px 15px' }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: '15px' }}>
          Add New Subtopic
        </Typography>
        <div>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography {...label}>Curriculum *</Typography>
                <Autocomplete
                  disableClearable
                  size="small"
                  value={curriculumData.selectedItem}
                  options={curriculumData.options}
                  loading={curriculumState.status === 'loading'}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  onChange={(event, value) =>
                    onSubjectMapChange({ curriculumOption: value })
                  }
                  renderInput={(params) =>
                    renderInput({
                      params,
                      isLoading: curriculumState.status === 'loading',
                      placeholder: 'Select Curriculum',
                    })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography {...label}>Grade *</Typography>
                <Autocomplete
                  disableClearable
                  size="small"
                  value={gradeData.selectedItem}
                  options={gradeData.options}
                  loading={curriculumState.status === 'loading'}
                  isOptionEqualToValue={(option, value) => option === value}
                  disabled={
                    !curriculumData.selectedItem ||
                    curriculumData.selectedItem === defaultEmptySelectedOption
                  }
                  onChange={(event, value) =>
                    onSubjectMapChange({
                      curriculumOption: curriculumData.selectedItem,
                      gradeOption: value,
                    })
                  }
                  renderInput={(params) =>
                    renderInput({
                      params,
                      isLoading: curriculumState.status === 'loading',
                      placeholder: 'Select Grade',
                    })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography {...label}>Subject *</Typography>
                <Autocomplete
                  disableClearable
                  size="small"
                  value={subjectData.selectedItem}
                  options={subjectData.options}
                  onChange={handleSubjectChange}
                  loading={curriculumState.status === 'loading'}
                  disabled={
                    !gradeData.selectedItem ||
                    gradeData.selectedItem === defaultEmptySelectedOption
                  }
                  renderInput={(params) =>
                    renderInput({
                      params,
                      isLoading: curriculumState.status === 'loading',
                      placeholder: 'Select Subject',
                    })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography {...label}>Subject Level *</Typography>
                <Autocomplete
                  size="small"
                  disableClearable
                  value={subjectLevelData.selectedItem}
                  options={subjectLevelData.options}
                  onChange={handleSubjectLevelChange}
                  loading={curriculumState.status === 'loading'}
                  disabled={
                    !subjectData.selectedItem ||
                    subjectData.selectedItem === defaultEmptySelectedOption
                  }
                  renderInput={(params) =>
                    renderInput({
                      params,
                      isLoading: curriculumState.status === 'loading',
                      placeholder: 'Select Subject Level',
                    })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography {...label}>Topic</Typography>
                <Autocomplete
                  fullWidth
                  size="small"
                  disableClearable
                  loading={topicState.status === 'loading'}
                  onChange={handleTopicChange}
                  options={topicData.options}
                  value={topicData.selectedItem}
                  disabled={
                    !subjectLevelData.selectedItem ||
                    subjectLevelData.selectedItem === defaultEmptySelectedOption
                  }
                  renderInput={(params) =>
                    renderInput({
                      params,
                      isLoading: topicState.status === 'loading',
                      placeholder: 'Select Topic',
                    })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography {...label}>Sub Topic *</Typography>
                <Autocomplete
                  fullWidth
                  size="small"
                  disableClearable
                  loading={subTopicState.status === 'loading'}
                  onChange={handleSubTopicChange}
                  options={subTopicData.options}
                  value={subTopicData.selectedItem}
                  disabled={
                    !topicData.selectedItem ||
                    topicData.selectedItem === defaultEmptySelectedOption
                  }
                  renderInput={(params) =>
                    renderInput({
                      params,
                      isLoading: subTopicState.status === 'loading',
                      placeholder: 'Select Subtopic',
                    })
                  }
                />
              </FormControl>
            </Grid>
            {topicData &&
              topicData.selectedItem.papers &&
              topicData.selectedItem.papers.length &&
              topicData.selectedItem.papers.map((paperItem, index) => {
                const isChecked = papers.some(
                  (paper) => paper === paperItem.name
                );

                return (
                  <Grid key={index} item xs={6}>
                    <FormControlLabel
                      label={paperItem.name}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={() =>
                            handlePaperChange(paperItem, isChecked)
                          }
                        />
                      }
                    />
                  </Grid>
                );
              })}
            <Grid item xs={12}>
              <div style={{ padding: '15px 0' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="contained"
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      color="primary"
                      variant="contained"
                      onClick={onSubmit}
                      disabled={
                        !papers.length ||
                        !subTopicData.selectedItem ||
                        subTopicData.selectedItem === defaultEmptySelectedOption
                      }
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>
              </div>
            </Grid>
          </Grid>
        </div>
      </Box>
    </Drawer>
  );
};

export default AddNewSubTopicDrawer;
